import { ClientsTable } from '@/components/features/clients/ClientsTable'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { UserRole, UserStatus } from '@/domain/auth/auth'
import { useClients, useClientsStats } from '@/hooks/api/useClients'
import { useServerPagination } from '@/hooks/useServerPagination'
import { Paths } from '@/routes/paths'
import { useAuthStore } from '@/store/useAuthStore'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { Filter, Plus, Search, UserCheck } from 'lucide-react'
import { PageContainer } from '@/components/features/shared/PageContainer'
import { NavHeader } from '@/components/features/shared/NavHeader'

export const ClientsManagment = () => {
  const navigate = useNavigate()
  const searchParams = useSearch({ strict: false }) as any
  const { hasAnyRole } = useAuthStore()

  const canEdit = hasAnyRole([UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler])
  const isEmployee = hasAnyRole([UserRole.Employee])

  const statusFilter = searchParams.status || 'all'

  const {
    pagination,
    setPagination: handlePaginationChange,
    search,
    inputValue,
    setInputValue,
    handleSearch,
    updateQueryParams,
    limit,
  } = useServerPagination({
    clearDefaults: {
      status: 'all',
    },
    resetOn: [statusFilter],
  })

  const { data: paginatedResponse, isLoading } = useClients({
    search: search || undefined,
    status: statusFilter === 'all' ? undefined : statusFilter as UserStatus,
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    includeDetails: true
  })

  const { data: stats } = useClientsStats({ enabled: !isEmployee })

  return (
    <PageContainer>
      <NavHeader hideBackButton
        actions={
          canEdit && (
            <Button onClick={() => navigate({ to: `${Paths.Management.NewClient}` })}>
              <Plus className="mr-2 h-4 w-4" />
              Nuevo Cliente
            </Button>
          )
        }
      >
        <h1 className="text-3xl font-bold tracking-tight">Clientes</h1>
        <p className="text-muted-foreground">
          Gestiona la información de tus clientes
        </p>
      </NavHeader>

      <div className="flex flex-col sm:flex-row items-center gap-4">
        <div className="flex w-full max-w-sm items-center space-x-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar clientes..."
              className="pl-8"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch()
                }
              }}
            />
          </div>
        </div>
        <Select value={statusFilter} onValueChange={(val) => {
          updateQueryParams({ status: val })
        }}>
          <SelectTrigger className="w-full sm:w-48">
            <Filter className="mr-2 h-4 w-4" />
            <SelectValue placeholder="Filtrar por estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value={UserStatus.Active}>Activos</SelectItem>
            <SelectItem value={UserStatus.Inactive}>Inactivos</SelectItem>
            <SelectItem value={UserStatus.Suspended}>Morosos</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={handleSearch} variant="info" className='ml-auto'>Buscar</Button>
      </div>

      {!isEmployee && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Clientes
            </CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total || 0}</div>
            <p className="text-xs text-muted-foreground">
              Total registrados
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Clientes Activos
            </CardTitle>
            <UserCheck className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.active || 0}</div>
            <p className="text-xs text-muted-foreground">
              {stats?.total ? Math.round((stats.active / stats.total) * 100) : 0}% del total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Con Deuda
            </CardTitle>
            <UserCheck className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.overdue || 0}</div>
            <p className="text-xs text-muted-foreground">
              Pagos pendientes
            </p>
          </CardContent>
        </Card>
        </div>
      )}

      <Card>
        <CardHeader className='pb-0'>
          <CardTitle>Lista de Clientes</CardTitle>
          <CardDescription>
            Administra y visualiza todos los clientes registrados
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <ClientsTable
              paginated={paginatedResponse}
              isLoading={isLoading}
              pagination={pagination}
              onPaginationChange={handlePaginationChange}
              onRowClick={(client) => navigate({ to: `${Paths.Management.Clients}/${client.id}` })}
            />
          </div>
        </CardContent>
      </Card>
    </PageContainer>
  )
}
